import React, {useMemo, useState} from 'react';
import {Button, message, Modal, Space, Tabs, Typography} from 'antd';
import {PlusOutlined} from '@ant-design/icons';
import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import {
  GET_POD_EXEMPT_EFFECTIVE_REPORTS,
  GET_POD_EXEMPT_EFFECTIVE_STATS,
  GET_POD_EXEMPT_SPOT_TASKS_FOR_DSP,
  PodExemptSpotTasksForDspResponse,
  GET_POD_EXEMPT_REPORTS,
  GET_POD_EXEMPT_REPORT_DETAIL,
  PodExemptEffectiveReportsResponse,
  PodExemptEffectiveStatsResponse,
  PodExemptReportDetailResponse,
  PodExemptReportsResponse,
  IS_POD_EXEMPT_REPORTER_BLOCKED,
  PodExemptReporterBlockedResponse,
} from '../../../graphql/podExempt/queries';
import {
  CLOSE_POD_EXEMPT_REPORT,
  ClosePodExemptReportResponse,
  OFFLINE_POD_EXEMPT_REPORT,
  OfflinePodExemptReportResponse,
} from '../../../graphql/podExempt/mutations';
import {
  PodExemptReportFiltersInput,
  PodExemptReportType,
  PodExemptStatus,
} from '../../../graphql/podExempt/types';
import PodExemptFilterBar from '../../../components/podExempt/PodExemptFilterBar';
import PodExemptReportTable from '../../../components/podExempt/PodExemptReportTable';
import PodExemptEffectiveTable from '../../../components/podExempt/PodExemptEffectiveTable';
import PodExemptSpotCheckTable from '../../../components/podExempt/PodExemptSpotCheckTable';
import PodExemptDetailDrawer from '../../../components/podExempt/PodExemptDetailDrawer';
import PodExemptReporterBlockedModal from '../../../components/podExempt/PodExemptReporterBlockedModal';
import TablePaginationBar from '../../../components/TablePaginationBar';
import {usePodExemptDict} from '../../../hooks/usePodExemptDict';
import {useWarehouseContext} from '../../../contexts/WarehouseContext';
import {translateGraphQLError} from '../../../utils/graphqlUtils';
import CreatePodExemptDrawer from './CreatePodExemptDrawer';

type PageTab = 'mine' | 'effective' | 'spotCheck';

/** 统计卡（对齐原型：标签在上、数值在下） */
const StatCard: React.FC<{label: string; value: React.ReactNode}> = ({label, value}) => (
  <div className="border border-gray-100 rounded-md px-4 py-2.5 min-w-[150px] bg-gray-50/40">
    <div className="text-xs text-gray-400">{label}</div>
    <div className="text-lg font-semibold mt-1">{value}</div>
  </div>
);

/** DSP 后台：门牌拍不到上报（本 DSP 数据域由服务端注入）。 */
const PodExemptListPage: React.FC = () => {
  const {t} = useTranslation();
  // 产品拍板：DSP 各页数据/可圈范围/提交归属都跟随侧边栏当前选中仓
  const {currentWarehouse} = useWarehouseContext();
  const warehouseId = currentWarehouse?.id ?? null;
  /**
   * 页签、筛选、分页都存 URL query，不放组件 state（与两个审核列表页同口径）。
   * 离开本页再回来（详情抽屉里跳走、浏览器后退、刷新）时原样复原，不用重新选一遍。
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page')) || 1;
  const pageSize = Number(searchParams.get('size')) || 10;
  const filters: PodExemptReportFiltersInput = useMemo(() => {
    const status = searchParams.get('status');
    return {
      statuses: status ? [status as PodExemptStatus] : null,
      addressType: searchParams.get('addressType') || null,
      keyword: searchParams.get('keyword') || null,
      addressKeyword: searchParams.get('addr') || null,
    };
  }, [searchParams]);
  /** 改页签/筛选/翻页统一走这里；传 null 即删除该参数（回到默认值），replace 避免堆历史 */
  const patchParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setSearchParams(next, {replace: true});
  };
  const [createOpen, setCreateOpen] = useState(false);
  // 点「新建上报」先查禁报：被禁账号不该填完表单、传完照片才被拒
  const [checkReporterBlocked, {loading: checkingReporterBlocked}] =
    useLazyQuery<PodExemptReporterBlockedResponse>(IS_POD_EXEMPT_REPORTER_BLOCKED, {fetchPolicy: 'network-only'});
  const handleOpenCreate = async () => {
    // 查询失败放行开表单，与后端 isReporterBlocked 的失败放行一致；提交时还会再校验
    const {data} = await checkReporterBlocked();
    if (data?.podExemptReporterBlocked) {
      setReporterBlockedOpen(true);
      return;
    }
    setCreateOpen(true);
  };
  const [reporterBlockedOpen, setReporterBlockedOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  /** 页签：mine=我的上报 / effective=已通过的地址 / spotCheck=抽查记录（数据来自外部抽查系统，接入后展示） */
  const pageTab = (searchParams.get('tab') as PageTab | null) ?? 'mine';

  const {data, loading, refetch} = useQuery<PodExemptReportsResponse>(GET_POD_EXEMPT_REPORTS, {
    variables: {filters: {...filters, warehouseId}, pageRequest: {page, size: pageSize}},
    fetchPolicy: 'cache-and-network',
    skip: pageTab !== 'mine',
  });
  const {data: effectiveData, loading: effectiveLoading, refetch: refetchEffective} =
    useQuery<PodExemptEffectiveReportsResponse>(GET_POD_EXEMPT_EFFECTIVE_REPORTS, {
      variables: {warehouseId, pageRequest: {page, size: pageSize}},
      fetchPolicy: 'cache-and-network',
      skip: pageTab !== 'effective',
    });
  const {data: statsData, refetch: refetchStats} = useQuery<PodExemptEffectiveStatsResponse>(GET_POD_EXEMPT_EFFECTIVE_STATS, {
    variables: {warehouseId},
    fetchPolicy: 'cache-and-network',
    skip: pageTab !== 'effective',
  });
  // 抽查记录：统计卡 + 列表由同一接口返回（后端刻意合并，避免卡片与列表口径漂移）
  const {data: spotData, loading: spotLoading} = useQuery<PodExemptSpotTasksForDspResponse>(
    GET_POD_EXEMPT_SPOT_TASKS_FOR_DSP,
    {
      variables: {pageRequest: {page, size: pageSize}},
      fetchPolicy: 'cache-and-network',
      skip: pageTab !== 'spotCheck',
    },
  );

  const {data: detailData, loading: detailLoading} = useQuery<PodExemptReportDetailResponse>(
    GET_POD_EXEMPT_REPORT_DETAIL,
    {variables: {reportId: detailId}, skip: detailId == null, fetchPolicy: 'network-only'},
  );

  const [closeReport] = useMutation<ClosePodExemptReportResponse>(CLOSE_POD_EXEMPT_REPORT);
  const [offlineReport] = useMutation<OfflinePodExemptReportResponse>(OFFLINE_POD_EXEMPT_REPORT);

  // 原因/地址类型/驳回模板展示名（全量字典拉一次做映射；缺 REJECT 会让流转记录显示裸 code）
  const {options: reasonDict, items: reasonDictAll} = usePodExemptDict('REASON');
  const {options: addressTypeDict, items: addressTypeDictAll} = usePodExemptDict('ADDRESS_TYPE');
  const {options: rejectDict, items: rejectDictAll} = usePodExemptDict('REJECT');
  const dictNameOf = useMemo(() => {
    const map = new Map<string, string>();
    [...reasonDictAll, ...addressTypeDictAll, ...rejectDictAll].forEach((item) => map.set(item.code, item.name));
    return (code: string) => map.get(code) ?? code;
  }, [reasonDictAll, addressTypeDictAll, rejectDictAll]);

  const handleClose = (record: PodExemptReportType) => {
    Modal.confirm({
      title: t('podExempt.close.confirmTitle'),
      content: t('podExempt.close.confirmContent', {reportNo: record.reportNo}),
      onOk: async () => {
        try {
          await closeReport({variables: {reportId: record.id}});
          message.success(t('podExempt.close.success'));
          refetch();
        } catch (e) {
          // 并发审核冲突等业务错误按 messageKey 翻译，不裸弹开发串
          message.error(translateGraphQLError(e, 'podExempt.close.failed'));
        }
      },
    });
  };

  const handleOffline = (record: PodExemptReportType) => {
    Modal.confirm({
      title: t('podExempt.offline.confirmTitle'),
      content: t('podExempt.offline.confirmContent', {display: record.displayAddress ?? record.reportNo}),
      okButtonProps: {danger: true},
      onOk: async () => {
        try {
          await offlineReport({variables: {reportId: record.id}});
          message.success(t('podExempt.offline.success'));
          refetchEffective();
          refetchStats();
        } catch (e) {
          message.error(translateGraphQLError(e, 'podExempt.offline.failed'));
        }
      },
    });
  };

  const spotPage = spotData?.podExemptSpotTasksForDsp;
  const spotStats = spotPage?.stats;
  const pageData = pageTab === 'effective' ? effectiveData?.podExemptEffectiveReports : data?.podExemptReports;
  const stats = statsData?.podExemptEffectiveStats;
  const canClose = (record: PodExemptReportType) =>
    record.status === 'PENDING_FIRST' || record.status === 'PENDING_FINAL';

  return (
    <div className="p-4">
      <Tabs
        activeKey={pageTab}
        onChange={(key) => {
          patchParams({tab: key === 'mine' ? null : key, page: null});
        }}
        items={[
          {key: 'mine', label: t('podExempt.pageTab.mine')},
          {key: 'effective', label: t('podExempt.pageTab.effective')},
          {key: 'spotCheck', label: t('podExempt.pageTab.spotCheck')},
        ]}
      />
      {pageTab === 'spotCheck' && (
        <>
          <div className="flex gap-3.5 flex-wrap pb-4">
            <StatCard
              label={t('podExempt.spotCheck.statBatch')}
              value={spotStats ? spotStats.batchCount : '-'}
            />
            <StatCard
              label={t('podExempt.spotCheck.statSample')}
              value={spotStats ? Number(spotStats.sampleCount) : '-'}
            />
            <StatCard
              label={t('podExempt.spotCheck.statFailRate')}
              // 分母为 0 时后端返回 null，显示「-」而非 0%（0% 会被误读为"抽了但全通过"）
              value={
                spotStats?.failRate != null ? `${(spotStats.failRate * 100).toFixed(1)}%` : '-'
              }
            />
            <StatCard
              label={t('podExempt.spotCheck.statOffline')}
              value={spotStats ? spotStats.offlineCount : '-'}
            />
          </div>
          <PodExemptSpotCheckTable
            data={spotPage?.content ?? []}
            loading={spotLoading}
          />
        </>
      )}
      {pageTab === 'mine' && (
        <>
          <PodExemptFilterBar
            filters={filters}
            onFiltersChange={(next) =>
              patchParams({
                status: next.statuses?.[0] ?? null,
                addressType: next.addressType ?? null,
                keyword: next.keyword ?? null,
                addr: next.addressKeyword ?? null,
                page: null,
              })
            }
            addressTypeOptions={addressTypeDict.map((item) => ({value: item.code, label: item.name}))}
            actionSlot={
              <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate} loading={checkingReporterBlocked}>
                {t('podExempt.create.action')}
              </Button>
            }
          />
          <PodExemptReportTable
            data={pageData?.content ?? []}
            loading={loading}
            dictNameOf={dictNameOf}
            onRowClick={(record) => setDetailId(record.id)}
            renderActions={(record) => (
              <Space size="middle">
                <Typography.Link onClick={() => setDetailId(record.id)}>
                  {t('podExempt.common.view')}
                </Typography.Link>
                {canClose(record) && (
                  <Typography.Link type="danger" onClick={() => handleClose(record)}>
                    {t('podExempt.close.action')}
                  </Typography.Link>
                )}
              </Space>
            )}
          />
        </>
      )}
      {pageTab === 'effective' && (
        <>
          <div className="flex gap-3.5 flex-wrap pb-4">
            <StatCard
              label={t('podExempt.effective.cardEffective')}
              value={Number(stats?.effectiveCount ?? 0)}
            />
            <StatCard
              label={t('podExempt.effective.cardHits')}
              value={Number(stats?.hit30dCount ?? 0)}
            />
          </div>
          <PodExemptEffectiveTable
            data={pageData?.content ?? []}
            loading={effectiveLoading}
            dictNameOf={dictNameOf}
            onView={(record) => setDetailId(record.id)}
            onOffline={handleOffline}
          />
        </>
      )}
      {pageTab === 'spotCheck' && (
        <TablePaginationBar
          total={Number(spotPage?.totalElements ?? 0)}
          current={page}
          pageSize={pageSize}
          onChange={(nextPage, nextSize) => {
            patchParams({
              page: nextPage > 1 ? String(nextPage) : null,
              size: nextSize !== 10 ? String(nextSize) : null,
            });
          }}
        />
      )}
      {pageTab !== 'spotCheck' && (
        <TablePaginationBar
          total={Number(pageData?.totalElements ?? 0)}
          current={page}
          pageSize={pageSize}
          onChange={(nextPage, nextSize) => {
            patchParams({
              page: nextPage > 1 ? String(nextPage) : null,
              size: nextSize !== 10 ? String(nextSize) : null,
            });
          }}
        />
      )}
      <CreatePodExemptDrawer
        warehouseId={warehouseId}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          refetch();
          refetchEffective();
        }}
      />
      <PodExemptDetailDrawer
        open={detailId != null}
        onClose={() => setDetailId(null)}
        detail={detailData?.podExemptReportDetail ?? null}
        loading={detailLoading}
        dictNameOf={dictNameOf}
      />
      <PodExemptReporterBlockedModal
        visible={reporterBlockedOpen}
        onClose={() => setReporterBlockedOpen(false)}
      />
    </div>
  );
};

export default PodExemptListPage;
